import { createRoot } from 'react-dom/client';
import { App } from './App';
import React, { StrictMode } from 'react';
import { AppCtxProvider, getPlannerConfig } from './AppContext';
import {
    createTheme,
    lighten,
    ThemeProvider
} from "@mui/material/styles";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';

const container = document.getElementById('app')!;
const shadowContainer = container.attachShadow({ mode: "open" });
const shadowRootElement = document.createElement("div");
shadowContainer.appendChild(shadowRootElement);
const print = {
    '@media print': {
        '@page': {
            size: 'A3 portrait',
            margin: '75px',
        },
        'body': {
            margin: 0,
            padding: 0,
        },
        '.hideOnPrint': {
            display: 'none!important',
        },
        '.MuiTab-root': {
            display: 'none',
        },
        '.MuiTabs-flexContainer': {
            display: 'block',
        },
        '.MuiTab-root.Mui-selected': {
            display: 'block',
            width: '70%',
            minWidth: 'fit-content',
        },
        '.MuiDataGrid-footerContainer': {
            display: 'none!important',
        },
        '.tabsBox': {
            flexDirection: 'row!important',
        },
        '*': {
            backgroundColor: 'rgba(0,0,0,0)!important',
            opacity: 1,
            color: 'black!important',
            borderColor: 'black!important',
            position: 'relative!important',

        }
    }
}
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const plannerConfig = getPlannerConfig();
const theme = createTheme((() => {
    const fontFamily = plannerConfig ? plannerConfig.font.family : 'Raleway';
    const faillback = plannerConfig ? plannerConfig.font.faillback : 'sans-serif';
    const style = plannerConfig ? plannerConfig.font.style : 'normal';
    const weight = plannerConfig ? plannerConfig.font.weight : 400;
    const primaryColor = plannerConfig ? plannerConfig.primaryColor : '#426b31';
    const primaryContrast = plannerConfig ? plannerConfig.primaryColorContrastText : '#ffffff';
    return {
        typography: {
            fontFamily: `${fontFamily}, ${faillback}`,
            style,
            faillback,
            fontWeightRegular: weight,
            htmlFontSize: 16,
        },
        palette: {
            primary: {
                main: primaryColor,

                light: lighten(primaryColor, .5), //'#96c65e',
                contrastText: primaryContrast

            },
            secondary: {
                main: '#e7ae1e'
            }
        },
        components: {
            MuiPopover: {
                defaultProps: {
                    container: shadowRootElement,
                    disableEnforceFocus: true,
                    disableAutoFocus: true,
                    disableScrollLock: true,
                },
            },
            MuiPopper: {
                defaultProps: {
                    container: shadowRootElement,
                },
            },
            MuiModal: {
                defaultProps: {
                    container: shadowRootElement,
                    disableEnforceFocus: true,
                    disableAutoFocus: true,
                    disableScrollLock: true,
                },
            },
            MuiDrawer: {
                defaultProps: {
                    container: shadowRootElement,
                    disableEnforceFocus: true,
                    disableAutoFocus: true,
                    disableScrollLock: true,
                },
            },
            MuiSelect: {
                defaultProps: {
                    MenuProps: {
                        PaperProps: {
                            style: {
                                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                                width: 250,
                            },
                        },
                        container: shadowRootElement,
                        disableAutoFocusItem: true,
                        disableEnforceFocus: true,
                        disableAutoFocus: true,
                        disableScrollLock: true,
                        disableRestoreFocus: true,
                        disablePortal: true,
                        keepMounted: true,
                        MenuListProps: {
                            disabledItemsFocusable: true
                        }
                    },
                }
            },
            MuiTooltip: {
                defaultProps: {
                    PopperProps: {
                        container: shadowRootElement,
                    },
                },
            },
            MuiMenu: {
                defaultProps: {
                    container: shadowRootElement,
                    disableAutoFocusItem: true,
                    disableEnforceFocus: true,
                    disableAutoFocus: true,
                    disableScrollLock: true,
                }
            },
            // @ts-ignore
            MuiDataGrid: {
                componentProps: {
                    basePopper: {
                        container: shadowRootElement,
                        disableAutoFocusItem: true,
                        disableEnforceFocus: true,
                        disableAutoFocus: true,
                        disableScrollLock: true,
                    },
                }
            },
            MuiDialog: {
                defaultProps: {
                    container: shadowRootElement,
                    disableEnforceFocus: true,
                    disableAutoFocus: true,
                    disableScrollLock: true,
                }
            },
        },
    }
})());
const cache = createCache({
    key: "css",
    prepend: true,
    container: shadowContainer
});
var app = document.getElementById('app');

if (app) {
    const root = createRoot(shadowRootElement);
    root.render(
        <StrictMode>
            <CacheProvider value={cache}>
                <ThemeProvider theme={theme}>
                    <GlobalStyles styles={{ ...print }} />
                    <CssBaseline />
                    <AppCtxProvider>
                        <App />
                    </AppCtxProvider>
                </ThemeProvider>
            </CacheProvider>
        </StrictMode>);
}